import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/Alert';
import { Search, Edit, CheckCircle, Eye, Calendar, Tag, Plus, X, Save } from 'lucide-react';
import { articleService, extractRegistrationListData } from '../../../api/articleService';
import authService from '../../../api/authService';
import {
  canApproveKbArticle,
  canIncludeKbDraftsInList,
  canPublishKbArticle,
  getArticlesScreenPermissions,
} from '../../../utils/kbScreenPermissions';
import toast from 'react-hot-toast';

const Drafts = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    content: '',
    category: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const currentUser = authService.getCurrentUser();
  const articlesPermissions = getArticlesScreenPermissions();
  const canSeeDraftArticles = canIncludeKbDraftsInList();
  const canApproveKbArticleAction = canApproveKbArticle();
  const canPublishKbArticleAction = canPublishKbArticle();
  const canPublishOrApproveDraft = canApproveKbArticleAction || canPublishKbArticleAction;

  const resolveTenantIdForArticles = () => {
    if (currentUser?.tenantId != null && currentUser?.tenantId !== '') {
      const n = Number(currentUser.tenantId);
      if (Number.isFinite(n)) return n;
    }
    const s = sessionStorage.getItem('tenantId');
    if (s) {
      const n = parseInt(s, 10);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  };

  const transformArticle = (article) => {
    const status = (article.status || 'draft').toLowerCase();
    return {
      id: `ART-${String(article.articleId).padStart(3, '0')}`,
      articleId: article.articleId,
      title: article.title || 'Untitled Article',
      status,
      originalStatus: status,
      category: article.category || 'General',
      views: article.usedCount || 0,
      helpful: article.helpfulCount || 0,
      createdDate: article.createdAt ? new Date(article.createdAt).toISOString().split('T')[0] : null,
      lastModified: article.updatedAt
        ? new Date(article.updatedAt).toISOString().split('T')[0]
        : (article.createdAt ? new Date(article.createdAt).toISOString().split('T')[0] : null),
      author: article.createdBy || 'Unknown',
      tags: article.tags || [],
      question: article.question || '',
      answer: article.answer || '',
      content: article.answer || article.question || '',
    };
  };

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = resolveTenantIdForArticles();
      const params = { tenantId };
      if (canSeeDraftArticles) {
        params.includeDrafts = true;
      }

      const response = await articleService.getArticles(params);
      const rows = extractRegistrationListData(response)
        .map(transformArticle)
        .filter((a) => (a.originalStatus || a.status || '').toLowerCase() === 'draft');

      setArticles(rows);
    } catch (err) {
      console.error('Error loading drafts:', err);
      const message = err?.message || 'Failed to load drafts';
      setError(message);
      toast.error(message);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [canSeeDraftArticles]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return articles
      .filter((a) => (a.originalStatus || a.status || '').toLowerCase() === 'draft')
      .filter((a) => {
        if (!term) return true;
        return (
          a.title.toLowerCase().includes(term)
          || (a.content || '').toLowerCase().includes(term)
          || (a.tags || []).some((tag) => tag.toLowerCase().includes(term))
        );
      });
  }, [articles, searchTerm]);

  const handlePublish = async (article) => {
    if (!article?.articleId) return;
    setActionBusy(true);
    try {
      if (canApproveKbArticleAction) {
        await articleService.approveArticle(article.articleId);
      } else if (canPublishKbArticleAction) {
        await articleService.updateArticle(article.articleId, {
          title: article.title,
          question: article.question || article.title,
          answer: article.answer || article.content || ' ',
          category: article.category,
          tags: article.tags,
          status: 'active',
        });
      } else {
        toast.error('You do not have permission to publish this draft.');
        return;
      }
      toast.success('Article published successfully');
      await loadDrafts();
    } catch (err) {
      console.error('Error publishing draft:', err);
      const message = err?.response?.data?.message || err?.message || 'Failed to publish article';
      toast.error(message);
    } finally {
      setActionBusy(false);
    }
  };

  const handleCreateDraft = async () => {
    setActionBusy(true);
    try {
      const createdBy = currentUser?.email || currentUser?.fullName || sessionStorage.getItem('userName') || 'agent';
      await articleService.createArticle({
        title: 'Untitled Article',
        question: 'Draft',
        answer: 'Content pending.',
        category: 'General',
        status: 'draft',
        tags: [],
        createdBy,
      });
      toast.success('Draft created');
      await loadDrafts();
    } catch (err) {
      console.error('Error creating draft:', err);
      toast.error(err?.message || 'Failed to create draft');
    } finally {
      setActionBusy(false);
    }
  };

  const handleEdit = (article) => {
    setEditingId(article.articleId);
    setEditForm({
      title: article.title,
      content: article.content,
      category: article.category,
      tags: [...(article.tags || [])],
    });
    setTagInput('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: '', content: '', category: '', tags: [] });
    setTagInput('');
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const article = articles.find((a) => a.articleId === editingId);
    if (!article) return;

    setActionBusy(true);
    try {
      await articleService.updateArticle(article.articleId, {
        title: editForm.title.trim(),
        question: article.question || editForm.title.trim(),
        answer: editForm.content.trim() || ' ',
        category: editForm.category.trim() || 'General',
        tags: editForm.tags,
        status: 'draft',
      });
      toast.success('Draft saved');
      handleCancelEdit();
      await loadDrafts();
    } catch (err) {
      console.error('Error saving draft:', err);
      toast.error(err?.message || 'Failed to save draft');
    } finally {
      setActionBusy(false);
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !editForm.tags.includes(tag)) {
      setEditForm((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tagToRemove) }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const categories = useMemo(() => {
    const fromArticles = [...new Set(articles.map((a) => a.category).filter(Boolean))];
    const defaults = [
      'General',
      'Technical Support',
      'Email Support',
      'Network Support',
      'Software Support',
      'Hardware Support',
      'Account Management',
      'Security',
    ];
    return [...new Set([...fromArticles, ...defaults])];
  }, [articles]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[240px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading drafts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Drafts</h1>
        <Button
          variant="primary"
          className="flex items-center gap-2"
          onClick={handleCreateDraft}
          disabled={actionBusy}
        >
          <Plus className="h-4 w-4" /> New Draft
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not load drafts</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-4">
        <div className="relative w-full md:w-1/3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            type="text"
            placeholder="Search drafts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 pr-3 py-2 border rounded-md w-full"
          />
        </div>
      </div>

      <div className="space-y-4">
        {filtered.length === 0 ? (
          <Alert>
            <AlertTitle>No Drafts</AlertTitle>
            <AlertDescription>
              You don&apos;t have any drafts yet. Create one to get started.
            </AlertDescription>
          </Alert>
        ) : (
          filtered.map((article) => (
            <Card key={article.articleId} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                {editingId === article.articleId ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Draft</h3>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Title <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                        placeholder="Enter article title"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Category
                      </label>
                      <select
                        value={editForm.category}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Content
                      </label>
                      <textarea
                        value={editForm.content}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, content: e.target.value }))}
                        placeholder="Enter article content..."
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Tags
                      </label>
                      <div className="flex gap-2 mb-2">
                        <Input
                          type="text"
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          placeholder="Add a tag and press Enter"
                          className="flex-1"
                        />
                        <Button variant="secondary" size="sm" onClick={handleAddTag} type="button">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {editForm.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {editForm.tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="flex items-center gap-1 cursor-pointer hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                              {tag}
                              <X className="h-3 w-3 ml-1" onClick={() => handleRemoveTag(tag)} />
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Button variant="ghost" size="sm" onClick={handleCancelEdit} disabled={actionBusy}>
                        Cancel
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleSaveEdit} disabled={actionBusy}>
                        <Save className="h-4 w-4 mr-1" /> Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{article.title}</h3>
                        <Badge variant="warning">Draft</Badge>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">ID:</span> {article.id} | {article.category}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {article.content || 'No content yet'}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4" /> {article.views} views
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" /> Modified: {article.lastModified || '—'}
                        </div>
                      </div>
                      {article.tags.length > 0 && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                          <Tag className="h-4 w-4" />
                          {article.tags.map((tag) => (
                            <Badge key={tag} variant="secondary" className="mr-1">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="mt-4 md:mt-0 flex flex-col items-end space-y-2">
                      <div className="flex gap-2">
                        {articlesPermissions.canUpdate && (
                        <Button variant="secondary" size="sm" onClick={() => handleEdit(article)} disabled={actionBusy}>
                          <Edit className="h-4 w-4 mr-1" /> Edit
                        </Button>
                        )}
                        {canPublishOrApproveDraft && (
                        <Button variant="primary" size="sm" onClick={() => handlePublish(article)} disabled={actionBusy}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {canApproveKbArticleAction ? 'Approve' : 'Publish'}
                        </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Drafts;
